import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { PdfService } from '../../services/pdf.service';
import { readFileAsync } from '@et/utils';

@Component({
  selector: 'et-pdf-viewer-page',
  imports: [NgClass],
  templateUrl: './pdf-viewer-page.component.html',
  styleUrl: './pdf-viewer-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerPageComponent implements AfterViewInit, OnChanges {
  private elementRef = inject(ElementRef);
  private pdfService = inject(PdfService);

  @Input({ required: true }) file!: File;
  @Input({ required: true }) showOverlay = false;
  @Input({ required: true }) page = 1;
  @Input() maxHeight = '100%';

  /** when passed page does not belong to the document adjustPage event is fired to parent */
  @Output() adjustPage = new EventEmitter<number>();

  @ViewChild('pdfCanvas') pdfCanvas!: ElementRef<HTMLCanvasElement>;

  ngAfterViewInit(): void {
    this.renderPage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['page'] && !changes['page'].firstChange) {
      this.loadPage(this.page);
    }
  }

  /**
   * Renders the PDF page.
   *
   * This function gets the width of the page, sets the PDF canvas, reads the file,
   * initializes the PDF service with the file URI and the canvas, and loads the page.
   *
   * @returns {Promise<void>} A promise that resolves when the page has been rendered.
   */
  private async renderPage(): Promise<void> {
    // Getting page width
    const pageWidth =
      this.elementRef.nativeElement.getBoundingClientRect().width - 2;

    // Set PDF canvas
    const pdfCanvas = this.pdfCanvas.nativeElement;
    const fileURI = await this.readFile(this.file);
    await this.pdfService.init(fileURI, pdfCanvas, pageWidth);
    const adjustedPageNumber = Math.min(
      this.pdfService.totalPages(),
      Math.max(1, this.page),
    );
    if (adjustedPageNumber !== this.page) {
      this.adjustPage.emit(adjustedPageNumber);
      return;
    }
    await this.loadPage(adjustedPageNumber);
  }

  /**
   * Loads a page of the PDF.
   *
   * @param {number} pageNumber - The number of the page to load.
   *
   * This function sets the current page number and calls the `renderPage` method of the `PdfService`
   * with the page number.
   *
   * @returns {Promise<void>} A promise that resolves when the page has been loaded.
   */
  private async loadPage(pageNumber: number): Promise<void> {
    this.page = pageNumber;
    await this.pdfService.renderPage(pageNumber);
  }

  /**
   * Reads a file or returns a string.
   *
   * @param {File | string} src - The file or string to read.
   *
   * If the source is a File, this function reads the file and returns its contents as a string.
   * If the source is a string, it returns the string.
   *
   * @returns {Promise<string>} A promise that resolves to the contents of the file or the string.
   */
  private async readFile(src: File | string): Promise<string> {
    if (src instanceof File) {
      return (await readFileAsync(src)) as string;
    }
    return src as string;
  }
}
